import React, { useState, useRef } from 'react';
import { Modal } from '../../../shared/components/Modal';
import { Button } from '../../../shared/components/Button';
import { COVER_TEMPLATES } from '../../../templates/cover';
import { CoverTemplate } from '../../../templates/types';
import { FileText, Check, ShieldCheck, Upload, ArrowRight, Layers, FileCheck } from 'lucide-react';
import { useToast } from '../../../shared/context/ToastContext';

interface AddCoverPageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyCoverTemplate: (template: CoverTemplate) => void;
  onUploadCustomCover?: (file: File, fileDataUrl: string) => void;
  hasExistingCover: boolean;
  onProceedToReport?: () => void;
}

export const AddCoverPageModal: React.FC<AddCoverPageModalProps> = ({
  isOpen,
  onClose,
  onApplyCoverTemplate,
  onUploadCustomCover,
  hasExistingCover,
  onProceedToReport,
}) => {
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<'template' | 'upload'>('template');
  const [selectedCoverId, setSelectedCoverId] = useState<string>(COVER_TEMPLATES[0]?.id || 'tpl-standard-lab-report');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedPreview, setUploadedPreview] = useState<string | null>(null);

  const handleApplyTemplate = () => {
    const tpl = COVER_TEMPLATES.find((t) => t.id === selectedCoverId);
    if (tpl) {
      onApplyCoverTemplate(tpl);
      showToast(`Applied "${tpl.name}" cover template.`, 'success');
      if (onProceedToReport) {
        onProceedToReport();
      }
    }
    onClose();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isDocx = file.name.toLowerCase().endsWith('.docx');
    const isImage = file.type.startsWith('image/');

    if (!isPdf && !isDocx && !isImage) {
      showToast('Please upload a PDF, Word DOCX, or image file.', 'warning');
      return;
    }

    setUploadedFile(file);

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setUploadedPreview(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleApplyUploadedCover = () => {
    if (!uploadedFile || !uploadedPreview) return;
    if (onUploadCustomCover) {
      onUploadCustomCover(uploadedFile, uploadedPreview);
    }
    showToast(`Attached custom cover "${uploadedFile.name}".`, 'success');
    if (onProceedToReport) {
      onProceedToReport();
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={hasExistingCover ? 'Configure Lab Report Cover Page' : 'Add Cover Page to Lab Report'}
      maxWidth="680px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>

          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {activeTab === 'template' ? (
              <Button
                variant="primary"
                icon={<ArrowRight size={14} />}
                iconPosition="right"
                onClick={handleApplyTemplate}
              >
                Apply Cover & Continue
              </Button>
            ) : (
              <Button
                variant="primary"
                icon={<ArrowRight size={14} />}
                iconPosition="right"
                disabled={!uploadedFile}
                onClick={handleApplyUploadedCover}
              >
                Attach Cover & Continue
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {/* Tab Navigation */}
        <div className="tabs-header">
          <button
            type="button"
            className={`tab-btn ${activeTab === 'template' ? 'active' : ''}`}
            onClick={() => setActiveTab('template')}
          >
            <Layers size={13} style={{ display: 'inline', marginRight: 4 }} />
            Choose Academic Template
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === 'upload' ? 'active' : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            <Upload size={13} style={{ display: 'inline', marginRight: 4 }} />
            Upload Custom Cover (PDF / DOCX)
          </button>
        </div>

        {activeTab === 'template' ? (
          <div>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
              Select a cover page template. Your course title, student name, instructor, and metadata will automatically carry over.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', maxHeight: '360px', overflowY: 'auto' }}>
              {COVER_TEMPLATES.map((tpl) => {
                const isSelected = selectedCoverId === tpl.id;
                return (
                  <div
                    key={tpl.id}
                    onClick={() => setSelectedCoverId(tpl.id)}
                    style={{
                      border: `2px solid ${isSelected ? 'var(--primary-500)' : 'var(--border-subtle)'}`,
                      borderRadius: 'var(--radius-md)',
                      padding: 'var(--space-3)',
                      backgroundColor: isSelected ? 'var(--primary-50)' : 'var(--bg-surface)',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      gap: 'var(--space-2)',
                      transition: 'all var(--transition-fast)',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span className="badge badge-neutral" style={{ fontSize: '10px' }}>
                          {tpl.thumbnailBadge || tpl.category.toUpperCase()}
                        </span>
                        {isSelected && <Check size={14} color="var(--primary-600)" />}
                      </div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: 'var(--font-size-xs)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FileText size={13} color="var(--primary-600)" />
                        {tpl.name}
                      </h4>
                      <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.3 }}>
                        {tpl.description}
                      </p>
                    </div>

                    <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <ShieldCheck size={11} /> Format: {tpl.defaultValues.borderStyle}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', margin: 0 }}>
              Already have an official cover sheet provided by your department? Upload your PDF, DOCX, or scanned sheet directly.
            </p>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".pdf,.docx,image/*"
              style={{ display: 'none' }}
            />

            <div
              style={{
                border: '2px dashed var(--border-default)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-8) var(--space-4)',
                textAlign: 'center',
                backgroundColor: 'var(--bg-canvas)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 'var(--space-2)',
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--primary-50)',
                  color: 'var(--primary-600)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Upload size={24} />
              </div>
              <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
                {uploadedFile ? uploadedFile.name : 'Click to select PDF, DOCX, or Image Cover'}
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                {uploadedFile ? `${(uploadedFile.size / 1024).toFixed(1)} KB` : 'Supports standard A4/Letter cover page formats'}
              </div>
            </div>

            {uploadedFile && (
              <div
                style={{
                  padding: 'var(--space-3) var(--space-4)',
                  backgroundColor: 'var(--color-success-bg)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-success)',
                }}
              >
                <FileCheck size={16} />
                <span>Ready to attach as the primary cover sheet for this report.</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};
